import { useQuery } from '@tanstack/react-query';
import { pb } from '@/core/api';
import type { Tier } from './use-pricing';

// Read hooks for the pricing-ranges model (fase 19b). Moved here from
// `modules/admin/hooks/` so non-admin modules (stats/progress bar milestones)
// can consume it without violating module isolation.

export type PricingRange = {
  id: string;
  range_min: number;
  range_max: number | null;
  range_label: string;
  tiers: Tier[];
  created: string;
  superseded_by: string | null;
  is_current: boolean;
};

export type PricingRangesResponse = {
  current_count: number;
  ranges: PricingRange[];
};

export const PRICING_RANGES_KEY = ['pricing-ranges'] as const;

export function usePricingRanges() {
  return useQuery({
    queryKey: PRICING_RANGES_KEY,
    queryFn: async (): Promise<PricingRangesResponse> =>
      pb.send('/api/pricing/ranges', { method: 'GET' }),
    staleTime: 30_000,
  });
}

export function useCurrentPricingRange() {
  const query = usePricingRanges();
  const current = query.data?.ranges.find((r) => r.is_current);
  return { ...query, data: current };
}

export function useRangeHistory(rangeId: string | null) {
  return useQuery({
    queryKey: ['pricing-range-history', rangeId],
    queryFn: async (): Promise<{ history: PricingRange[] }> =>
      pb.send(`/api/pricing/ranges/${rangeId}/history`, { method: 'GET' }),
    staleTime: 60_000,
    enabled: !!rangeId,
  });
}
